import { Subject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, take, mapTo, takeUntil } from 'rxjs/operators';

import { AnimationEvent } from '@angular/animations';
import { CdkPortalOutlet, ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import {
  OnDestroy,
  ViewChild,
  Component,
  ComponentRef,
  EmbeddedViewRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { snDrawerTransition } from '../../animations';
import { SnDrawerMode, SnDrawerPosition, SnDrawerAnimationState } from '../../types';

@Component({
  selector: 'sn-drawer-container',
  template: '<ng-template cdkPortalOutlet></ng-template>',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [snDrawerTransition],
  host: {
    class: 'sn-drawer-container',
    '[class.sn-drawer-container--right]': 'position === "right"',
    '[class.sn-drawer-container--modal]': 'mode === "modal"',
    '[class.sn-drawer-container--side]': 'mode === "side"',
    '[style.width]': 'width',
    '[@snDrawerTransition]': 'animationState',
    '(@snDrawerTransition.start)': 'animationStarted($event)',
    '(@snDrawerTransition.done)': 'animationEnded($event)',
  },
})
export class SnDrawerContainer implements OnDestroy {
  @ViewChild(CdkPortalOutlet, { static: true }) private _portalOutlet: CdkPortalOutlet;

  private _width: string | number;
  private _mode: SnDrawerMode;
  private _position: SnDrawerPosition;

  private _opened: boolean = true;
  private readonly _openedChange$: Subject<boolean> = new Subject();

  private _animationState: SnDrawerAnimationState = 'open';
  private readonly _animationStarted$: Subject<AnimationEvent> = new Subject();
  private readonly _animationEnded$: Subject<AnimationEvent> = new Subject();

  private readonly _destroy$ = new Subject<void>();

  constructor(private readonly _changeDetectorRef: ChangeDetectorRef) {
    this._animationEnded$
      .pipe(
        distinctUntilChanged((x, y) => x.fromState === y.fromState && x.toState === y.toState),
        filter(({ fromState, toState }) => {
          return (
            (toState === 'open' && fromState === 'void') ||
            (toState === 'void' && fromState === 'open')
          );
        }),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._openedChange$.next(this._opened);
      });
  }

  get openedChange$(): Observable<boolean> {
    return this._openedChange$.asObservable();
  }

  get openStarted$(): Observable<void> {
    return this._animationStarted$.pipe(
      filter((event) => event.fromState !== event.toState && event.toState === 'open'),
      mapTo(undefined),
    );
  }

  get opened$(): Observable<void> {
    return this._openedChange$.pipe(
      filter((opened) => opened),
      mapTo(undefined),
    );
  }

  get closeStarted$(): Observable<void> {
    return this._animationStarted$.pipe(
      filter((event) => event.fromState !== event.toState && event.toState === 'void'),
      mapTo(undefined),
    );
  }

  get closed$(): Observable<void> {
    return this._openedChange$.pipe(
      filter((opened) => !opened),
      mapTo(undefined),
    );
  }

  get animationStarted$(): Observable<AnimationEvent> {
    return this._animationStarted$.asObservable();
  }

  get animationEnded$(): Observable<AnimationEvent> {
    return this._animationEnded$.asObservable();
  }

  get width(): string | number {
    return this._width;
  }
  set width(value: string | number) {
    value = typeof value === 'number' ? `${value}px` : value;

    if (value !== this._width) {
      this._width = value;
      this._changeDetectorRef.markForCheck();
    }
  }

  get mode(): SnDrawerMode {
    return this._mode;
  }
  set mode(value: SnDrawerMode) {
    if (value !== this._mode) {
      this._mode = value;
      this._changeDetectorRef.markForCheck();
    }
  }

  get position(): SnDrawerPosition {
    return this._position;
  }
  set position(value: SnDrawerPosition) {
    if (value !== this._position) {
      this._position = value;
      this._changeDetectorRef.markForCheck();
    }
  }

  get animationState(): SnDrawerAnimationState {
    return this._animationState;
  }

  public ngOnDestroy(): void {
    this._openedChange$.complete();
    this._animationStarted$.complete();
    this._animationEnded$.complete();

    this._destroy$.next();
    this._destroy$.complete();
  }

  public open(): Observable<'open' | 'close'> {
    return this.toggle(true);
  }

  public close(): Observable<'open' | 'close'> {
    return this.toggle(false);
  }

  public toggle(isOpen: boolean = !this._opened): Observable<'open' | 'close'> {
    this._opened = isOpen;

    this._animationState = isOpen ? 'open' : 'void';

    return this._openedChange$.pipe(
      take(1),
      map((opened) => (opened ? 'open' : 'close')),
    );
  }

  public attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
    return this._portalOutlet.attachComponentPortal(portal);
  }

  public attachTemplatePortal<T>(portal: TemplatePortal<T>): EmbeddedViewRef<T> {
    return this._portalOutlet.attachTemplatePortal(portal);
  }

  public animationStarted(event: AnimationEvent): void {
    this._animationStarted$.next(event);
  }

  public animationEnded(event: AnimationEvent): void {
    this._animationEnded$.next(event);
  }
}
